import React, { useState, useEffect } from 'react';
import '../styles/node-selector.css';

// src/components/NodeSelector.jsx

/**
 * Component for selecting a node before RWA purchase
 */
const NodeSelector = ({ 
  nodes, 
  onSelectNode, 
  selectedNodeId,
  isLoading,
  error,
  productTier,
  wallet
}) => {
  const [filterTier, setFilterTier] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredNodes, setFilteredNodes] = useState([]);
  
  // Filter and sort nodes
  useEffect(() => {
    if (!nodes || nodes.length === 0) {
      setFilteredNodes([]);
      return;
    }
    
    let filtered = [...nodes];
    
    // Filter by tier if specified
    if (filterTier !== 'all') {
      filtered = filtered.filter(node => 
        node.tier_id === filterTier || 
        (filterTier === 'apex' && node.tier_level === 4) ||
        (filterTier === 'master' && node.tier_level === 3) ||
        (filterTier === 'super' && node.tier_level === 2) ||
        (filterTier === 'standard' && node.tier_level === 1)
      );
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(node => 
        node.reference_code.toLowerCase().includes(query) ||
        node.wallet_address.toLowerCase().includes(query) ||
        (node.full_name && node.full_name.toLowerCase().includes(query))
      );
    }
    
    // Sort by tier level (highest first)
    filtered.sort((a, b) => {
      // First sort by tier level (4,3,2,1)
      const tierDiff = (b.tier_level || 0) - (a.tier_level || 0);
      if (tierDiff !== 0) return tierDiff;
      
      // Then by active status
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      
      // Finally by creation date (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });
    
    setFilteredNodes(filtered);
  }, [nodes, filterTier, searchQuery]);
  
  const handleFilterChange = (tier) => {
    setFilterTier(tier);
  };
  
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Function to display the tier label
  const getTierLabel = (tier) => {
    switch(tier) {
      case 'apex': return 'Apex Node (Level 4)';
      case 'master': return 'Master Node (Level 3)';
      case 'super': return 'Super Node (Level 2)';
      case 'standard': return 'Standard Node (Level 1)';
      default: return tier;
    }
  };
  
  // Get recommended tier for the product
  const getRecommendedTierText = () => {
    if (!productTier) return null;
    
    let recommendedTier;
    switch(productTier) {
      case 'enterprise': recommendedTier = 'apex'; break;
      case 'elite': recommendedTier = 'master'; break;
      case 'pro': recommendedTier = 'super'; break;
      case 'basic': recommendedTier = 'standard'; break;
      default: return null;
    }
    
    return (
      <div className="recommended-tier-info">
        <div className="recommended-badge">RECOMMENDED</div>
        <p>For this product, we recommend selecting a <strong>{getTierLabel(recommendedTier)}</strong> for optimal performance and rewards.</p>
      </div>
    );
  };
  
  // Function to truncate wallet address
  const truncateAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };
  
  // Function to check if node belongs to user
  const isUserNode = (nodeWalletAddress) => {
    if (!wallet || !wallet.address || !nodeWalletAddress) return false;
    return nodeWalletAddress.toLowerCase() === wallet.address.toLowerCase();
  };
  
  // Display tier filter buttons
  const renderTierFilters = () => (
    <div className="node-tier-filters">
      <button 
        className={`tier-filter ${filterTier === 'all' ? 'active' : ''}`} 
        onClick={() => handleFilterChange('all')}
      >
        All Tiers
      </button>
      <button 
        className={`tier-filter ${filterTier === 'apex' ? 'active' : ''}`} 
        onClick={() => handleFilterChange('apex')}
      >
        Apex
      </button>
      <button 
        className={`tier-filter ${filterTier === 'master' ? 'active' : ''}`} 
        onClick={() => handleFilterChange('master')}
      >
        Master
      </button>
      <button 
        className={`tier-filter ${filterTier === 'super' ? 'active' : ''}`} 
        onClick={() => handleFilterChange('super')}
      >
        Super
      </button>
      <button 
        className={`tier-filter ${filterTier === 'standard' ? 'active' : ''}`} 
        onClick={() => handleFilterChange('standard')}
      >
        Standard
      </button>
    </div>
  );
  
  if (isLoading) {
    return (
      <div className="node-selector-loading">
        <div className="spinner"></div>
        <p>Loading available nodes...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="node-selector-error">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 9v3m0 3h.01m-6.938 3h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 15c-.77 1.333.192 3 1.732 3z" 
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <p>{error}</p>
      </div>
    );
  }
  
  return (
    <div className="node-selector">
      <div className="node-selector-header">
        <h3>Select a Node for this Purchase</h3>
        <p>Your RWA device will be linked to the selected node for rewards and governance.</p>
        
        {getRecommendedTierText()}
      </div>
      
      <div className="node-selector-search">
        <input
          type="text"
          placeholder="Search by reference code or wallet address..."
          value={searchQuery}
          onChange={handleSearch}
        />
      </div>
      
      {renderTierFilters()}
      
      <div className="node-list-container">
        {filteredNodes.length === 0 ? (
          <div className="no-nodes-message">
            <p>No nodes found matching your criteria.</p>
          </div>
        ) : (
          <div className="node-list">
            {filteredNodes.map(node => (
              <div 
                key={node.id}
                className={`node-item ${selectedNodeId === node.id ? 'selected' : ''} ${isUserNode(node.wallet_address) ? 'own-node' : ''}`}
                onClick={() => onSelectNode(node.id)}
              >
                <div className="node-item-content">
                  <div className="node-info">
                    <div className="node-reference-code">{node.reference_code}</div>
                    <div className="node-wallet">{truncateAddress(node.wallet_address)}</div>
                  </div>
                  
                  <div className="node-badges">
                    <div className={`node-tier-badge tier-${node.tier_id || node.tier_level}`}>
                      {node.tier_name || getTierLabel(node.tier_id)}
                    </div>
                    
                    {isUserNode(node.wallet_address) && (
                      <div className="node-own-badge">YOUR NODE</div>
                    )}
                  </div>
                </div>
                
                {selectedNodeId === node.id && (
                  <div className="node-selected-indicator">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeSelector;
